"use client"

import { useState, useEffect } from "react"
import { ShieldQuestion, Wrench, Users, Lightbulb, PenTool, Rocket, CheckCircle2 } from "lucide-react"
import { useLanguage } from "./LanguageContext"
import { loadAppConfiguration } from "./ContentLoader"
import { useSeo } from "./hooks/useSeo"
import Navigation, { Footer } from "./Navigation"
import MissieDemo from "./components/MissieDemo"
import "./css/AboutPage.css"
import "./css/MissieDemo.css"
import "./css/MissiesPage.css"
import "./css/ResourcesPage.css"

const CONTENT_BASE = "/games/missies/content"

type ChapterMeta = {
  id: string
  title: string
  subtitle: string
  intro?: { tagline?: string; villainLine?: string; heroLine?: string }
}

type ConceptMeta = {
  id: string
  skill?: string
  skillLine?: string
}

// Strip stray em dashes from game-content copy pulled onto the marketing site.
const clean = (s?: string) => (s || "").replace(/\s*—\s*/g, ", ")

const PROGRESS_PATH_CONCEPTS = ["define", "personas", "idea-framing", "paper-sketch", "flow-logic", "prototype", "test"]

const VERB_ICONS = [ShieldQuestion, Wrench, Users, Lightbulb, PenTool, Rocket, CheckCircle2]

// Chapter count is dynamic (more chapters are coming), spelled out as a
// word for small counts so the heading reads naturally either way.
const COUNT_WORDS_NL = ["nul", "een", "twee", "drie", "vier", "vijf", "zes", "zeven", "acht", "negen", "tien"]
const COUNT_WORDS_EN = ["zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten"]
const countWord = (n: number, code: string) => {
  const words = code === "nl" ? COUNT_WORDS_NL : COUNT_WORDS_EN
  return words[n] || String(n)
}

const MissiesPage = ({ onReturnHome }: { onReturnHome?: () => void }) => {
  const { language } = useLanguage()
  const [appConfig, setAppConfig] = useState<any>(null)
  const [chapters, setChapters] = useState<ChapterMeta[]>([])
  const [pathConcepts, setPathConcepts] = useState<ConceptMeta[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const t = (path: string, defaultText = "") => {
    if (!appConfig) return defaultText
    const keys = path.split(".")
    let value: any = appConfig
    for (const key of keys) {
      if (!value || !value[key]) return defaultText
      value = value[key]
    }
    if (value && typeof value === "object" && value[language.code]) return value[language.code]
    return typeof value === "string" ? value : value?.en || defaultText
  }

  useSeo({
    path: "/missies",
    title:
      language.code === "en"
        ? "Techtok Missions: learn to make things digitally by playing"
        : "Techtok Missies: leer digitaal maken door te spelen",
    description:
      language.code === "en"
        ? "The Techtok mission path: chapter by chapter, kids play story missions, solve design problems, and build their own app idea. No account needed."
        : "De missielijn van Techtok: hoofdstuk voor hoofdstuk spelen kinderen verhaalmissies, lossen ontwerpproblemen op en bouwen hun eigen app-idee. Geen account nodig.",
  })

  useEffect(() => {
    const load = async () => {
      setIsLoading(true)
      try {
        const config = await loadAppConfiguration(language.code, null, false)
        setAppConfig(config)

        const manifestRes = await fetch(`${CONTENT_BASE}/chapters-manifest.json`)
        const manifest = manifestRes.ok ? await manifestRes.json() : { chapters: [] }

        const chapterData: (ChapterMeta | null)[] = await Promise.all(
          (manifest.chapters || []).map(async (id: string) => {
            const res = await fetch(`${CONTENT_BASE}/chapters/${id}/chapter.json`)
            return res.ok ? await res.json() : null
          })
        )
        setChapters(chapterData.filter((c): c is ChapterMeta => Boolean(c)))

        const conceptData: (ConceptMeta | null)[] = await Promise.all(
          PROGRESS_PATH_CONCEPTS.map(async (id) => {
            const res = await fetch(`${CONTENT_BASE}/chapters/app-ontwerper/concepts/${id}.json`)
            return res.ok ? await res.json() : null
          })
        )
        setPathConcepts(conceptData.filter((c): c is ConceptMeta => Boolean(c)))
      } catch (e) {
        console.error("Kon Missies-inhoud niet laden:", e)
      } finally {
        setIsLoading(false)
      }
    }
    load()
  }, [language.code])

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    )
  }

  return (
    <div className="landing-selector-container">
      <Navigation appConfig={appConfig} currentPage="none" onNavigate={() => {}} t={t} />

      <main className="main-content">
        <section className="missies-page-hero">
          <div className="missies-page-hero-inner">
            <h1 className="missies-page-title">
              {language.code === "nl" ? "Wat doe je eigenlijk in een missie?" : "What do you actually do in a mission?"}
            </h1>
            <p className="missies-page-subtitle">
              {language.code === "nl"
                ? "Geen lange uitleg. Techtok Missies zet je meteen aan het werk."
                : "No long explanations. Techtok Missies puts you to work right away."}
            </p>
            <a href="/games/missies/" className="main-action-button">
              {t("interface.playMissions", "Speel Techtok Missies")}
            </a>
          </div>
        </section>

        <section className="article-exit-banner">
          <div className="article-exit-banner-inner">
            <div className="article-exit-banner-copy">
              <h2>{language.code === "nl" ? "App Ontwerper: ons vlaggenschip" : "App Designer: our flagship"}</h2>
              <p>
                {language.code === "nl"
                  ? "Elke missie in App Ontwerper brengt je een stap dichter bij je eigen, echte app: van eerste idee tot werkend prototype. Crasher daagt je uit, jij bewijst hem ongelijk. Geen filmpje, geen screenshot: dit is precies wat je kind speelt."
                  : "Every mission in App Designer brings you one step closer to your own, real app: from first idea to working prototype. Crasher challenges you, you prove him wrong. No video, no screenshot: this is exactly what your child plays."}
              </p>
              <a href="/games/missies/" className="article-exit-banner-cta">
                {t("interface.playMissions", "Speel Techtok Missies")}
              </a>
            </div>
            <div className="article-exit-banner-demo">
              <MissieDemo conceptId="app-basics" loop={1} />
            </div>
          </div>
        </section>

        {chapters.length > 0 && (
          <section className="missies-chapters-section">
            <h2 className="how-it-works-title">
              {language.code === "nl"
                ? `${countWord(chapters.length, "nl")[0].toUpperCase()}${countWord(chapters.length, "nl").slice(1)} hoofdstukken, ${countWord(chapters.length, "nl")} vaardigheden`
                : `${countWord(chapters.length, "en")[0].toUpperCase()}${countWord(chapters.length, "en").slice(1)} chapters, ${countWord(chapters.length, "en")} skills`}
            </h2>
            <div className="missies-chapters-grid">
              {chapters.map((chapter) => (
                <div key={chapter.id} className="missies-chapter-card">
                  <h3>{chapter.title}</h3>
                  <p className="missies-chapter-subtitle">{chapter.subtitle}</p>
                  {chapter.intro?.tagline && <p className="missies-chapter-tagline">{clean(chapter.intro.tagline)}</p>}
                </div>
              ))}
            </div>
          </section>
        )}

        {pathConcepts.length > 0 && (
          <section className="missies-progress-section">
            <p className="how-it-works-demo-label">
              {language.code === "nl" ? "In het hoofdstuk App Ontwerper" : "In the App Designer chapter"}
            </p>
            <h2 className="missies-progress-title">
              {language.code === "nl" ? "Elke missie brengt je dichter bij jouw eigen app" : "Every mission brings you closer to your own app"}
            </h2>
            <div className="missies-progress-path">
              {pathConcepts.map((concept, i) => {
                const Icon = VERB_ICONS[i] || CheckCircle2
                return (
                  <div key={concept.id} className="missies-progress-step">
                    <div className="missies-progress-icon">
                      <Icon />
                    </div>
                    <span className="missies-progress-skill">{concept.skill}</span>
                  </div>
                )
              })}
            </div>
          </section>
        )}

        <section className="missies-cta-section">
          <h2>{language.code === "nl" ? "Klaar om te beginnen?" : "Ready to start?"}</h2>
          <a href="/games/missies/" className="article-exit-banner-cta">
            {t("interface.playMissions", "Speel Techtok Missies")}
          </a>
        </section>
      </main>

      <Footer appConfig={appConfig} t={t} />
    </div>
  )
}

export default MissiesPage
